import React, { useState } from 'react';
import styled from 'styled-components';
import { woocommerceService } from '../../../services/woocommerceService';
import { woocommerceServiceV2 } from '../../../services/woocommerceServiceV2';

const TestContainer = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TestButton = styled.button`
  background-color: #007cba;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 1rem 0.5rem;
  
  &:hover {
    background-color: #005a87;
  }
`;

const ResultContainer = styled.div`
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 0.9rem;
  max-height: 400px;
  overflow-y: auto;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #2e7d32;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border-left: 4px solid #c62828;
`;

const AuthMethodTest = () => {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const testBasicAuth = async () => {
    setLoading(true);
    setError('');
    setResult('Testing Basic Authentication Method...\n\n');
    
    try {
      const products = await woocommerceService.getProducts({ per_page: 1 });
      setResult(prev => prev + `✅ Basic Auth Success! Found ${products.length} products\n`);
      setResult(prev => prev + `First product: ${products[0]?.name || 'N/A'}\n\n`);
    } catch (err) {
      setResult(prev => prev + `❌ Basic Auth Failed: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const testQueryAuth = async () => {
    setLoading(true);
    setError('');
    setResult('Testing Query Parameter Authentication Method...\n\n');
    
    try {
      const products = await woocommerceServiceV2.getProducts({ per_page: 1 });
      setResult(prev => prev + `✅ Query Auth Success! Found ${products.length} products\n`);
      setResult(prev => prev + `First product: ${products[0]?.name || 'N/A'}\n\n`);
    } catch (err) {
      setResult(prev => prev + `❌ Query Auth Failed: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  const testBothMethods = async () => {
    setLoading(true);
    setError('');
    setResult('Testing Both Authentication Methods...\n\n');
    
    // Test Basic Auth
    setResult(prev => prev + 'Method 1: Basic Authentication\n');
    try {
      const basicProducts = await woocommerceService.getProducts({ per_page: 1 });
      setResult(prev => prev + `✅ Basic Auth Success! Found ${basicProducts.length} products\n\n`);
    } catch (err) {
      setResult(prev => prev + `❌ Basic Auth Failed: ${err.message}\n\n`);
    }

    // Test Query Auth
    setResult(prev => prev + 'Method 2: Query Parameter Authentication\n');
    try {
      const queryProducts = await woocommerceServiceV2.getProducts({ per_page: 1 });
      setResult(prev => prev + `✅ Query Auth Success! Found ${queryProducts.length} products\n\n`);
    } catch (err) {
      setResult(prev => prev + `❌ Query Auth Failed: ${err.message}\n\n`);
    }

    setResult(prev => prev + '🎯 Test Complete!\n\n');
    setResult(prev => prev + 'Recommendations:\n');
    setResult(prev => prev + '1. If Basic Auth works: Use the original service\n');
    setResult(prev => prev + '2. If Query Auth works: Use the V2 service\n');
    setResult(prev => prev + '3. If both fail: Check WooCommerce Legacy API settings\n');
    setResult(prev => prev + '4. If neither works: Check API key permissions\n');
    
    setLoading(false);
  };

  const testConnection = async () => {
    setLoading(true);
    setError('');
    setResult('Testing API Connection...\n\n');
    
    try {
      const connection = await woocommerceServiceV2.testConnection();
      if (connection.success) {
        setResult(prev => prev + `✅ Connection successful!\n`);
        setResult(prev => prev + `WooCommerce Version: ${connection.data.environment?.version || 'Unknown'}\n`);
        setResult(prev => prev + `WordPress Version: ${connection.data.environment?.wp_version || 'Unknown'}\n\n`);
      } else {
        setResult(prev => prev + `❌ Connection failed: ${connection.error}\n\n`);
      }
    } catch (err) {
      setResult(prev => prev + `❌ Connection test error: ${err.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TestContainer>
      <h2>WooCommerce Authentication Method Test</h2>
      <p>This page tests different authentication methods to find the one that works with your WooCommerce setup.</p>
      
      <div style={{ marginBottom: '2rem' }}>
        <TestButton onClick={testBasicAuth} disabled={loading}>
          Test Basic Auth
        </TestButton>
        <TestButton onClick={testQueryAuth} disabled={loading}>
          Test Query Auth
        </TestButton>
        <TestButton onClick={testBothMethods} disabled={loading}>
          Test Both Methods
        </TestButton>
        <TestButton onClick={testConnection} disabled={loading}>
          Test Connection
        </TestButton>
      </div>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      {result && (
        <ResultContainer>
          {result}
        </ResultContainer>
      )}
    </TestContainer>
  );
};

export default AuthMethodTest;